import React, { createContext, useContext, useState, useEffect } from 'react';

export type ThemeMode = 'dark' | 'light' | 'easy';

export interface ThemeColors {
  mode: ThemeMode;
  navyDark: string;
  navyPrimary: string;
  pageBg: string;
  cardBg: string;
  textColor: string;
  titleColor: string;
  textMutedColor: string;
  goldPrimary: string;
  goldMuted: string;
  borderThemeColor: string;
  headerBg: string;
  subHeaderBg: string;
  inputBg: string;
}

interface ThemeContextType {
  themeMode: ThemeMode;
  setThemeMode: (mode: ThemeMode) => void;
  colors: ThemeColors;
}

const getColors = (mode: ThemeMode): ThemeColors => {
  if (mode === 'light') {
    return {
      mode: 'light',
      navyDark: '#1e293b',
      navyPrimary: '#334155',
      pageBg: '#f8fafc',
      cardBg: '#ffffff',
      textColor: '#1e293b',
      titleColor: '#0f172a',
      textMutedColor: '#475569',
      goldPrimary: '#d97706',
      goldMuted: 'rgba(217, 119, 6, 0.3)',
      borderThemeColor: '#cbd5e1',
      headerBg: '#0f172a',
      subHeaderBg: '#1e293b',
      inputBg: '#ffffff'
    };
  }
  if (mode === 'easy') {
    return {
      mode: 'easy',
      navyDark: '#142421',
      navyPrimary: '#1b332d',
      pageBg: '#f4eedd',
      cardBg: '#fbf7ee',
      textColor: '#243632',
      titleColor: '#0d1f1c',
      textMutedColor: '#526661',
      goldPrimary: '#b45309',
      goldMuted: 'rgba(180, 83, 9, 0.35)',
      borderThemeColor: 'rgba(180, 83, 9, 0.25)',
      headerBg: '#0f1c19',
      subHeaderBg: '#172b27',
      inputBg: '#fbf7ee'
    };
  }
  // Default Dark Mode
  return {
    mode: 'dark',
    navyDark: '#070b12',
    navyPrimary: '#0f172a',
    pageBg: '#0a0f1d',
    cardBg: '#1e293b',
    textColor: '#e2e8f0',
    titleColor: '#ffffff',
    textMutedColor: '#94a3b8',
    goldPrimary: '#ecc94b',
    goldMuted: 'rgba(236, 201, 75, 0.4)',
    borderThemeColor: 'rgba(236,201,75,0.3)',
    headerBg: '#04070d',
    subHeaderBg: '#0d1527',
    inputBg: '#0f172a'
  };
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [themeMode, setThemeModeState] = useState<ThemeMode>(() => {
    const saved = localStorage.getItem('theme_mode');
    if (saved === 'light' || saved === 'easy' || saved === 'dark') return saved;
    return 'dark';
  });

  const setThemeMode = (mode: ThemeMode) => {
    setThemeModeState(mode);
    localStorage.setItem('theme_mode', mode);
    // Notify all components in current & other tabs
    window.dispatchEvent(new CustomEvent('themeChange', { detail: mode }));
  };

  useEffect(() => {
    const handleThemeChange = (e: any) => {
      const mode = e.detail || localStorage.getItem('theme_mode');
      if (mode === 'light' || mode === 'easy' || mode === 'dark') {
        setThemeModeState(mode as ThemeMode);
      }
    };
    window.addEventListener('themeChange', handleThemeChange);
    window.addEventListener('storage', handleThemeChange);
    return () => {
      window.removeEventListener('themeChange', handleThemeChange);
      window.removeEventListener('storage', handleThemeChange);
    };
  }, []);

  const colors = getColors(themeMode);

  return (
    <ThemeContext.Provider value={{ themeMode, setThemeMode, colors }}>
      <div style={{ backgroundColor: colors.pageBg, color: colors.textColor, minHeight: '100vh', transition: 'background-color 0.3s ease, color 0.3s ease' }}>
        {children}
      </div>
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    const saved = (localStorage.getItem('theme_mode') as ThemeMode) || 'dark';
    const mode = (saved === 'light' || saved === 'easy') ? saved : 'dark';
    return {
      themeMode: mode,
      setThemeMode: (m: ThemeMode) => {
        localStorage.setItem('theme_mode', m);
        window.dispatchEvent(new CustomEvent('themeChange', { detail: m }));
      },
      colors: getColors(mode)
    };
  }
  return context;
};
